import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  useTheme,
  InputAdornment,
  IconButton,
  styled,
  CircularProgress,
  DialogActions,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  getRef,
  getRefEdu,
  getSAPPickList,
  setSnackbarOpen,
  uploadFileJp,
} from "src/redux/actions";
import {
  CheckCircle,
  Close,
  DoneOutline,
  InfoOutlined,
  Download,
} from "@mui/icons-material";
import CustomAutocomplete from "../CustomAutocomplete";
import { loadImage } from "src/functions/utils";
import { CustomAutoComplete } from "src/pages/admin/Setting/EditPanel/ComponentGroup";
import CustomDatePicker from "../CustomDatePicker";

const CustomDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    backgroundColor: "rgb(255,255,255)",
    borderRadius: "1rem",
    border: "1px solid lightgrey",
    // maxWidth: "350px",
  },
}));

const CustomInputA = React.memo(function CustomInputA({
  label,
  name,
  onChange,
  value,
  defaultValue,
  required,
  ...rest
}) {
  return (
    <TextField
      label={label}
      value={value}
      defaultValue={defaultValue}
      name={name}
      onChange={onChange}
      size="small"
      variant="outlined"
      InputLabelProps={{ shrink: true }}
      fullWidth
      style={{ marginBottom: "1rem" }}
      {...rest}
    />
  );
});

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Daftar field wajib diisi (SEMUA field mandatory KECUALI BPJS Ketenagakerjaan)
const REQUIRED_FIELDS = [
  { key: "sap_birthplace_json", label: "Tempat Lahir" },
  { key: "sap_nationality_json", label: "Kebangsaan" },
  { key: "sap_suku_json", label: "Suku" },
  { key: "sap_religion_json", label: "Agama" },
  { key: "sap_blood_group_json", label: "Golongan Darah" },
  { key: "sap_marital_status_json", label: "Status Pernikahan" },
  { key: "address1", label: "Alamat" },
  { key: "sap_province_json", label: "Provinsi" },
  { key: "sap_cities_json", label: "Kota/Kabupaten" },
  { key: "sap_kecamatan_json", label: "Kecamatan" },
  { key: "kodepos_text", label: "Kode Pos" },
  { key: "educational_code", label: "Jenjang Pendidikan" },
  { key: "sap_university_json", label: "Nama Sekolah/Universitas" },
  { key: "sap_major_json", label: "Jurusan" },
  { key: "sap_city_univ_json", label: "Kota (Pendidikan)" },
  { key: "started_at", label: "Tanggal Mulai" },
  { key: "finished_at", label: "Tanggal Lulus" },
  { key: "sap_shirt_size_json", label: "Ukuran Baju" },
  { key: "sap_pants_size_json", label: "Ukuran Celana" },
  { key: "sap_shoes_size_json", label: "Ukuran Sepatu" },
  { key: "bpjs_k_number", label: "No. BPJS Kesehatan" },
  { key: "bpjs_k_path", label: "Upload BPJS Kesehatan" },
  { key: "npwp_number", label: "No. NPWP" },
  { key: "npwp_path", label: "Upload NPWP" },
  { key: "sap_tax_status_json", label: "Tax Status" },
  { key: "bank_account_path", label: "Upload Bukti Rekening" },
  { key: "bank_account_name", label: "Nama Pemilik Rekening" },
  { key: "bank_account_number", label: "No Rekening" },
  { key: "bank_id", label: "Bank" },
  // NOTE: bpjs_tk_number & bpjs_tk_path SENGAJA tidak dimasukkan (tidak wajib)
];

const isEmptyValue = (val) => {
  if (val === undefined || val === null) return true;
  if (typeof val === "string") return val.trim() === "";
  return false;
};

const ModalSignContractRequired = ({
  open,
  onClose,
  dataDetail,
  setDataDetail,
  handleSave,
  loadingButton,
}) => {
  useTheme();
  const [loading, setLoading] = useState(true);
  const [refBajuSize, setRefBajuSize] = useState([]);
  const [refCelanaSize, setRefCelanaSize] = useState([]);
  const [refTaxStatus, setRefTaxStatus] = useState([]);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [birthPlace, setBirthplace] = useState([]);
  const [nationality, setNationality] = useState([]);
  const [suku, setSuku] = useState([]);
  const [religion, setReligion] = useState([]);
  const [maritalStatus, setMaritalStatus] = useState([]);
  const [bloodGroup, setBloodGroup] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [kecamatan, setKecamatan] = useState([]);
  const [error, setError] = useState(false);
  const [eduStage, setEduStage] = useState([]);
  const [sapUniversity, setSapUniversity] = useState([]);
  const [sapMajor, setSapMajor] = useState([]);
  const [refBank, setRefBank] = useState([]);

  // === state error untuk validasi mandatory ===
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchRef();
  }, []);

  const clearError = useCallback((key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  const handleChange = useCallback((key, value) => {
    if (key === "kodepos_text") {
      if (!/^\d*$/.test(value)) return;

      setDataDetail((prev) => {
        const updated = { ...prev, [key]: value };
        return updated;
      });
      setError(value.length !== 5);
      clearError("kodepos_text");
      return;
    }

    if (key === "address1" || key === "address2" || key === "address3") {
      setDataDetail((prev) => {
        const updated = { ...prev, [key]: value };
        updated.address = `${updated.address1 || ""}|${updated.address2 || ""}|${updated.address3 || ""}`;
        return updated;
      });
      clearError(key);
      return;
    }

    setDataDetail((prev) => ({ ...prev, [key]: value }));
    clearError(key);
  }, []);

  const fetchRef = async () => {
    setLoading(true);
    const res = await Promise.all([
      getSAPPickList({ pickList: "Z_EC_UniformSize_Baju" }),
      getSAPPickList({ pickList: "Z_EC_UniformSize_Celana" }),
      getSAPPickList({ pickList: "Z_EC_TaxStatus" }),
      getSAPPickList({ pickList: "Z_EC_TempatLahir" }),
      getSAPPickList({ pickList: "ISOCountryList" }),
      getSAPPickList({ pickList: "Z_EC_Suku" }),
      getSAPPickList({ pickList: "RELIGION_IDN" }),
      getSAPPickList({ pickList: "MaritalStatus" }),
      getSAPPickList({ pickList: "BLOODGROUP" }),
      getSAPPickList({ pickList: "Z_PROVINSI_IDN" }),
      getSAPPickList({ pickList: "Z_CITY_IDN" }),
      getSAPPickList({ pickList: "Z_KECAMATAN_IDN" }),
      getRefEdu(),
      getSAPPickList({ pickList: "UNIV_INA" }),
      getSAPPickList({ pickList: "major" }),
      getRef({ type: "refBank" }),
    ]);
    setLoading(false);
    setRefBajuSize(res[0].data?.results);
    setRefCelanaSize(res[1].data?.results);
    setRefTaxStatus(res[2].data?.results);
    setBirthplace(res[3].data?.results);
    setNationality(res[4].data?.results);
    setSuku(res[5].data?.results);
    setReligion(res[6].data?.results);
    setMaritalStatus(res[7].data?.results);
    setBloodGroup(res[8].data?.results);
    setProvinces(res[9].data?.results);
    setCities(res[10].data?.results);
    setKecamatan(res[11].data?.results);
    setEduStage(res[12].data);
    setSapUniversity(res[13].data?.results);
    setSapMajor(res[14].data?.results);
    res[15].data?.map((d) => {
      d.title = `${d.bank_id} - ${d.bank_name}`;
    });
    setRefBank(res[15].data || []);
  };

  const handleChangeSAP = (val, keyIdSAP, keyDb, keyJson) => {
    setDataDetail({
      ...dataDetail,
      [keyDb]: val[keyIdSAP],
      [keyJson]: val,
    });
    clearError(keyJson);
  };

  const handleDownload = async (uri) => {
    const url = await loadImage(uri);
    var link = document.createElement("a");
    link.href = url;
    link.click();
  };

  const handleUpload = async (files, key, old_file) => {
    if (files.length) {
      const validPdfTypes = ["application/pdf"];
      if (!validPdfTypes.includes(files[0].type)) {
        setSnackbarOpen("error", "Hanya file pdf yang diperbolehkan!");
        return;
      }

      setLoadingUpload(true);
      const resUpload = await uploadFileJp(files[0], old_file);
      if (typeof resUpload === "string") {
        setLoadingUpload(false);
        setSnackbarOpen("error", resUpload);
        return;
      }
      setDataDetail({
        ...dataDetail,
        [key]: resUpload?.data?.path,
      });
      clearError(key);
      setLoadingUpload(false);
      setSnackbarOpen("success", "Data tersimpan");
    }
  };

  const handleUploadBerkas = async (files, key, old_file) => {
    if (files.length) {
      const validPdfTypes = ["application/pdf"];
      if (!validPdfTypes.includes(files[0].type)) {
        setSnackbarOpen("error", "Hanya file pdf yang diperbolehkan!");
        return;
      }

      setLoadingUpload(true);
      const resUpload = await uploadFileJp(files[0], old_file);
      if (typeof resUpload === "string") {
        setLoadingUpload(false);
        setSnackbarOpen("error", resUpload);
        return;
      }
      const copyDt = { ...dataDetail };
      copyDt.sign_contract_files.map((sc) => {
        if (sc.job_posting_extension_form_id === key) {
          sc.uploaded_path = resUpload?.data?.path;
        }
      });
      setDataDetail(copyDt);
      // setDataDetail({
      //   ...dataDetail,
      //   [key]: resUpload?.data?.path,
      // });
      clearError(`sign_contract_${key}`);
      setLoadingUpload(false);
      setSnackbarOpen("success", "Data tersimpan");
    }
  };

  const handleDateChange = (val, name) => {
    setDataDetail({ ...dataDetail, [name]: val });
    clearError(name);
  };

  const getKecamatanAsync = async (input) => {
    const picklist = await getSAPPickList({
      pickList: "Z_KECAMATAN_IDN",
      filter: `and (label_en_US like '${input.toUpperCase()}' or label_en_US like '%${input.toUpperCase()}' or label_en_US like '${input.toUpperCase()}%' or label_en_US like '%${input.toUpperCase()}%')`,
    });
    return picklist.data?.results;
  };

  const getUnivAsync = async (input) => {
    const picklist = await getSAPPickList({
      pickList: "UNIV_INA",
      filter: `and (label_en_US like '${input.toUpperCase()}' or label_en_US like '%${input.toUpperCase()}' or label_en_US like '${input.toUpperCase()}%' or label_en_US like '%${input.toUpperCase()}%')`,
    });
    return picklist.data?.results;
  };

  // === Validasi semua field mandatory (kecuali BPJS TK) ===
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    REQUIRED_FIELDS.forEach(({ key, label }) => {
      if (isEmptyValue(dataDetail?.[key])) {
        newErrors[key] = `${label} wajib diisi`;
        isValid = false;
      }
    });

    if (
      !isEmptyValue(dataDetail?.kodepos_text) &&
      dataDetail.kodepos_text.length !== 5
    ) {
      newErrors.kodepos_text = "Kode Pos harus 5 digit";
      isValid = false;
    }

    (dataDetail?.sign_contract_files || []).forEach((sc) => {
      if (!sc.uploaded_path) {
        newErrors[`sign_contract_${sc.job_posting_extension_form_id}`] =
          `${sc.name} wajib diupload`;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    const isValid = validateForm();
    if (!isValid) {
      setSnackbarOpen("error", "Mohon lengkapi semua data yang wajib diisi");
      return;
    }
    handleSave();
  };

  if (loading) return <CircularProgress />;

  return (
    <CustomDialog open={open} maxWidth="sm" fullWidth>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <DialogTitle>
          <b>Sign Contract & Data Validation</b>
        </DialogTitle>
        <IconButton sx={{ mr: "1rem" }} onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>
      <DialogContent dividers>
        <Grid container>
          <Grid item sm={12}>
            <CustomInputA
              label="Email"
              placeholder="Email"
              name="email"
              value={dataDetail?.email}
              disabled
            />
            <CustomInputA
              label="No HP"
              placeholder="08xxx"
              name="mobile_phone"
              value={dataDetail?.mobile_phone}
              disabled
            />
            <CustomAutocomplete
              name="birth_place"
              label="Tempat Lahir *"
              options={birthPlace}
              labelKey={"label_en_US"}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_birthplace_id",
                  "sap_birthplace_json",
                )
              }
              value={dataDetail?.sap_birthplace_json || null}
              disableClearable
              error={!!errors.sap_birthplace_json}
              helperText={errors.sap_birthplace_json}
            />
            <CustomAutocomplete
              name="sap_nationality_id"
              label="Kebangsaan *"
              options={nationality}
              labelKey={"label_en_US"}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_nationality_id",
                  "sap_nationality_json",
                )
              }
              value={dataDetail?.sap_nationality_json || null}
              disableClearable
              error={!!errors.sap_nationality_json}
              helperText={errors.sap_nationality_json}
            />
            <CustomAutocomplete
              name="sap_suku_id"
              label="Suku *"
              options={suku}
              labelKey={"label_en_US"}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_suku_id",
                  "sap_suku_json",
                )
              }
              value={dataDetail?.sap_suku_json || null}
              disableClearable
              error={!!errors.sap_suku_json}
              helperText={errors.sap_suku_json}
            />
            <CustomAutocomplete
              name="sap_religion_id"
              label="Agama *"
              options={religion}
              labelKey={"label_en_US"}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_religion_id",
                  "sap_religion_json",
                )
              }
              value={dataDetail?.sap_religion_json || null}
              disableClearable
              error={!!errors.sap_religion_json}
              helperText={errors.sap_religion_json}
            />
            <CustomAutocomplete
              name="sap_blood_group_id"
              label="Golongan Darah *"
              options={bloodGroup}
              labelKey={"label_en_US"}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_blood_group_id",
                  "sap_blood_group_json",
                )
              }
              value={dataDetail?.sap_blood_group_json || null}
              disableClearable
              error={!!errors.sap_blood_group_json}
              helperText={errors.sap_blood_group_json}
            />
            <CustomAutocomplete
              name="sap_marital_status_id"
              label="Status Pernikahan *"
              options={maritalStatus}
              labelKey={"label_en_US"}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_marital_status_id",
                  "sap_marital_status_json",
                )
              }
              value={dataDetail?.sap_marital_status_json || null}
              disableClearable
              error={!!errors.sap_marital_status_json}
              helperText={errors.sap_marital_status_json}
            />
            <Stack
              sx={{
                border: "2px solid #e5e7eb",
                borderRadius: "16px",
                p: "1rem",
                mb: "1rem",
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
                Alamat Domisili Lengkap
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: "#6b7280", mb: "0.75rem" }}
              >
                Tuliskan alamat lengkap Anda. Jika alamat melebihi batas
                karakter, lanjutkan penulisan di kolom berikutnya (Alamat 2,
                Alamat 3).
              </Typography>

              {/* Address 1 - max 60 karakter */}
              <CustomInputA
                label="Alamat *"
                name="address1"
                inputProps={{ maxLength: 60 }}
                onChange={(e) =>
                  handleChange("address1", e.target.value.replace(/\|/g, ""))
                }
                value={dataDetail?.address1 || ""}
                error={!!errors.address1}
                helperText={
                  errors.address1 ||
                  `${(dataDetail?.address1 || "").length}/60 karakter`
                }
                onKeyDown={(e) => {
                  if (e.key === "|") {
                    e.preventDefault();
                  }
                }}
              />

              {/* Address 2 - max 40 karakter */}
              <CustomInputA
                label="Lanjutan Alamat (jika ada)"
                name="address2"
                inputProps={{ maxLength: 40 }}
                onChange={(e) =>
                  handleChange("address2", e.target.value.replace(/\|/g, ""))
                }
                value={dataDetail?.address2 || ""}
                helperText={`${(dataDetail?.address2 || "").length}/40 karakter`}
                sx={{ mt: "0.75rem" }}
                onKeyDown={(e) => {
                  if (e.key === "|") {
                    e.preventDefault();
                  }
                }}
              />

              {/* Address 3 - max 40 karakter */}
              <CustomInputA
                label="Lanjutan Alamat (jika ada)"
                name="address3"
                inputProps={{ maxLength: 40 }}
                onChange={(e) =>
                  handleChange("address3", e.target.value.replace(/\|/g, ""))
                }
                value={dataDetail?.address3 || ""}
                helperText={`${(dataDetail?.address3 || "").length}/40 karakter`}
                sx={{ mt: "0.75rem" }}
                onKeyDown={(e) => {
                  if (e.key === "|") {
                    e.preventDefault();
                  }
                }}
              />
              <CustomAutocomplete
                label="Provinsi *"
                options={provinces}
                labelKey={"label_en_US"}
                onChange={(e, val) =>
                  handleChangeSAP(
                    val,
                    "externalCode",
                    "sap_province_id",
                    "sap_province_json",
                  )
                }
                value={dataDetail?.sap_province_json || null}
                disableClearable
                error={!!errors.sap_province_json}
                helperText={errors.sap_province_json}
              />
              <CustomAutocomplete
                label="Kota/Kabupaten *"
                options={cities}
                labelKey={"label_en_US"}
                onChange={(e, val) =>
                  handleChangeSAP(
                    val,
                    "externalCode",
                    "sap_cities_id",
                    "sap_cities_json",
                  )
                }
                value={dataDetail?.sap_cities_json || null}
                disableClearable
                error={!!errors.sap_cities_json}
                helperText={errors.sap_cities_json}
              />
              <CustomAutocomplete
                label="Kecamatan *"
                // options={kecamatan}
                fetchOptions={(input) => getKecamatanAsync(input)}
                labelKey={"label_en_US"}
                onChange={(e, val) =>
                  handleChangeSAP(
                    val,
                    "externalCode",
                    "sap_kecamatan_id",
                    "sap_kecamatan_json",
                  )
                }
                value={dataDetail?.sap_kecamatan_json || null}
                disableClearable
                error={!!errors.sap_kecamatan_json}
                helperText={errors.sap_kecamatan_json}
              />
              <CustomInputA
                label="Kode Pos *"
                name="kodepos_text"
                onChange={(e) => handleChange("kodepos_text", e.target.value)}
                value={dataDetail?.kodepos_text}
                style={{ marginTop: "1rem" }}
                inputProps={{ maxLength: 5 }}
                error={
                  !!errors.kodepos_text ||
                  (error && dataDetail?.kodepos_text > 0)
                }
                helperText={
                  errors.kodepos_text ||
                  (error && dataDetail?.kodepos_text > 0 ? "Harus 5 digit" : "")
                }
              />
            </Stack>

            <Stack
              sx={{
                border: "2px solid #e5e7eb",
                borderRadius: "16px",
                p: "1rem",
                mb: "1rem",
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
                Isi Pendidikan yang Dilamar
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                Jenjang Pendidikan *
              </Typography>
              <CustomAutoComplete
                name="educational_code"
                optionsArr={eduStage}
                keyLabel={"educational_name"}
                handleSelect={(val, key) =>
                  handleChange("educational_code", val?.educational_code)
                }
                register="true"
                style={{ marginBottom: "1rem" }}
                value={
                  eduStage.filter(
                    (ed) =>
                      ed.educational_code === dataDetail?.educational_code,
                  )[0]
                }
                disableClearable
                error={!!errors.educational_code}
                helperText={errors.educational_code}
              />
              <CustomAutocomplete
                name="sap_university_id"
                label="Nama Sekolah/Universitas *"
                options={sapUniversity}
                placeholder={"ketik nama kampus"}
                fetchOptions={(input) => getUnivAsync(input)}
                labelKey={"label_en_US"}
                onChange={(e, val) =>
                  handleChangeSAP(
                    val,
                    "externalCode",
                    "sap_university_id",
                    "sap_university_json",
                  )
                }
                value={dataDetail?.sap_university_json || null}
                disableClearable
                error={!!errors.sap_university_json}
                helperText={errors.sap_university_json}
              />
              <CustomAutocomplete
                name="sap_major_id"
                label="Jurusan *"
                options={sapMajor}
                labelKey={"label_en_US"}
                onChange={(e, val) =>
                  handleChangeSAP(
                    val,
                    "externalCode",
                    "sap_major_id",
                    "sap_major_json",
                  )
                }
                value={dataDetail?.sap_major_json || null}
                disableClearable
                error={!!errors.sap_major_json}
                helperText={errors.sap_major_json}
              />
              <CustomAutocomplete
                label="Kota *"
                options={cities}
                labelKey={"label_en_US"}
                onChange={(e, val) =>
                  handleChangeSAP(
                    val,
                    "externalCode",
                    "sap_city_univ_id",
                    "sap_city_univ_json",
                  )
                }
                value={dataDetail?.sap_city_univ_json || null}
                disableClearable
                error={!!errors.sap_city_univ_json}
                helperText={errors.sap_city_univ_json}
              />
              <CustomDatePicker
                label="Tanggal Mulai *"
                onChange={handleDateChange}
                name="started_at"
                value={dataDetail.started_at ? dataDetail.started_at : null}
                error={!!errors.started_at}
                helperText={errors.started_at}
              />
              <CustomDatePicker
                label="Tanggal Lulus *"
                onChange={handleDateChange}
                name="finished_at"
                value={dataDetail.finished_at ? dataDetail.finished_at : null}
                error={!!errors.finished_at}
                helperText={errors.finished_at}
              />
            </Stack>
            <CustomAutocomplete
              options={refBajuSize}
              label="Ukuran Baju *"
              labelKey={"label_defaultValue"}
              value={dataDetail?.sap_shirt_size_json || null}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_shirt_size_id",
                  "sap_shirt_size_json",
                )
              }
              disableClearable
              error={!!errors.sap_shirt_size_json}
              helperText={errors.sap_shirt_size_json}
            />
            <CustomAutocomplete
              options={refCelanaSize}
              label="Ukuran Celana *"
              labelKey={"label_defaultValue"}
              value={dataDetail?.sap_pants_size_json || null}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_pants_size_id",
                  "sap_pants_size_json",
                )
              }
              disableClearable
              error={!!errors.sap_pants_size_json}
              helperText={errors.sap_pants_size_json}
            />
            <CustomAutocomplete
              options={refCelanaSize}
              label="Ukuran Sepatu *"
              labelKey={"label_defaultValue"}
              value={dataDetail?.sap_shoes_size_json || null}
              onChange={(e, val) =>
                handleChangeSAP(
                  val,
                  "externalCode",
                  "sap_shoes_size_id",
                  "sap_shoes_size_json",
                )
              }
              disableClearable
              error={!!errors.sap_shoes_size_json}
              helperText={errors.sap_shoes_size_json}
            />
          </Grid>

          <Grid item sm={12}>
            <Typography
              variant="subtitle1"
              sx={{ my: "1rem", fontWeight: "bold" }}
            >
              BPJS
            </Typography>
          </Grid>
          <Grid container spacing={1}>
            {dataDetail?.bpjs_k_path && (
              <Grid item sm={1}>
                <DoneOutline color="success" />
              </Grid>
            )}
            <Grid item sm={dataDetail?.bpjs_k_path ? 8 : 9}>
              <CustomInputA
                label="No. BPJS Kesehatan *"
                placeholder="No. BPJS Kesehatan"
                name="bpjs_k_number"
                onChange={(e) => handleChange("bpjs_k_number", e.target.value)}
                inputProps={{ maxLength: 13 }}
                error={!!errors.bpjs_k_number}
                helperText={errors.bpjs_k_number}
              />
            </Grid>
            <Grid item sm={3}>
              <Button component="label" variant="outlined">
                Upload <span style={{ fontSize: "10px" }}>{` (pdf)`}</span>
                <VisuallyHiddenInput
                  type="file"
                  accept="application/pdf"
                  onChange={(e) =>
                    handleUpload(
                      e.target.files,
                      "bpjs_k_path",
                      dataDetail?.bpjs_k_path,
                    )
                  }
                />
              </Button>
              {errors.bpjs_k_path && (
                <Typography sx={{ color: "#d32f2f", fontSize: "12px" }}>
                  {errors.bpjs_k_path}
                </Typography>
              )}
            </Grid>

            {dataDetail?.bpjs_tk_path && (
              <Grid item sm={1}>
                <DoneOutline color="success" />
              </Grid>
            )}
            <Grid item sm={dataDetail?.bpjs_tk_path ? 8 : 9}>
              <CustomInputA
                label="No. BPJS Ketenagakerjaan"
                placeholder="No. BPJS Ketenagakerjaan"
                name="bpjs_tk_number"
                onChange={(e) => handleChange("bpjs_tk_number", e.target.value)}
                inputProps={{ maxLength: 11 }}
              />
            </Grid>
            <Grid item sm={3}>
              <Button component="label" variant="outlined">
                Upload <span style={{ fontSize: "10px" }}>{` (pdf)`}</span>
                <VisuallyHiddenInput
                  type="file"
                  accept="application/pdf"
                  onChange={(e) =>
                    handleUpload(
                      e.target.files,
                      "bpjs_tk_path",
                      dataDetail?.bpjs_tk_path,
                    )
                  }
                />
              </Button>
            </Grid>
          </Grid>

          <Grid item sm={12}>
            <Typography
              variant="subtitle1"
              sx={{ my: "1rem", fontWeight: "bold" }}
            >
              NPWP
            </Typography>
          </Grid>
          <Grid container spacing={1}>
            {dataDetail?.npwp_path && (
              <Grid item sm={1}>
                <DoneOutline color="success" />
              </Grid>
            )}
            <Grid item sm={dataDetail?.npwp_path ? 8 : 9}>
              <CustomInputA
                label="No. NPWP *"
                placeholder="No. NPWP"
                name="npwp"
                inputProps={{ maxLength: 16 }}
                onChange={(e) => handleChange("npwp_number", e.target.value)}
                error={!!errors.npwp_number}
                helperText={errors.npwp_number}
              />
            </Grid>
            <Grid item sm={3}>
              <Button component="label" variant="outlined">
                Upload <span style={{ fontSize: "10px" }}>{` (pdf)`}</span>
                <VisuallyHiddenInput
                  type="file"
                  accept="application/pdf"
                  onChange={(e) =>
                    handleUpload(
                      e.target.files,
                      "npwp_path",
                      dataDetail?.npwp_path,
                    )
                  }
                />
              </Button>
              {errors.npwp_path && (
                <Typography sx={{ color: "#d32f2f", fontSize: "12px" }}>
                  {errors.npwp_path}
                </Typography>
              )}
            </Grid>
            <Grid item sm={9} sx={{ pt: "0 !important" }}>
              <CustomAutocomplete
                options={refTaxStatus}
                label="Tax Status *"
                labelKey={"label_localized"}
                value={dataDetail?.sap_tax_status_json || null}
                onChange={(e, val) =>
                  handleChangeSAP(
                    val,
                    "externalCode",
                    "sap_tax_status_id",
                    "sap_tax_status_json",
                  )
                }
                disableClearable
                error={!!errors.sap_tax_status_json}
                helperText={errors.sap_tax_status_json}
              />
            </Grid>
          </Grid>

          <Grid item sm={12}>
            <Typography
              variant="subtitle1"
              sx={{ my: "1rem", fontWeight: "bold" }}
            >
              Informasi Rekening Bank
            </Typography>
          </Grid>
          <Grid container spacing={1}>
            {dataDetail?.bank_account_path && (
              <Grid item sm={1}>
                <DoneOutline color="success" />
              </Grid>
            )}
            <Grid item sm={dataDetail?.bank_account_path ? 11 : 12}>
              <Button component="label" variant="outlined">
                Upload Bukti Rekening Milik Sendiri{" "}
                <span style={{ fontSize: "10px" }}>{` (pdf)`}</span>
                <VisuallyHiddenInput
                  type="file"
                  accept="application/pdf"
                  onChange={(e) =>
                    handleUpload(
                      e.target.files,
                      "bank_account_path",
                      dataDetail?.bank_account_path,
                    )
                  }
                />
              </Button>
              {errors.bank_account_path && (
                <Typography sx={{ color: "#d32f2f", fontSize: "12px" }}>
                  {errors.bank_account_path}
                </Typography>
              )}
            </Grid>

            <Grid item sm={12} sx={{ mt: "1rem" }}>
              <CustomInputA
                label="Nama Pemilik Rekening *"
                placeholder="Nama Pemilik Rekening"
                name="bank_account_name"
                onChange={(e) =>
                  handleChange("bank_account_name", e.target.value)
                }
                error={!!errors.bank_account_name}
                helperText={errors.bank_account_name}
              />
            </Grid>
            <Grid item sm={12}>
              <CustomInputA
                label="No Rekening *"
                placeholder="No Rekening"
                name="bank_account_number"
                onChange={(e) =>
                  handleChange("bank_account_number", e.target.value)
                }
                error={!!errors.bank_account_number}
                helperText={errors.bank_account_number}
              />
            </Grid>
            <Grid item sm={9}>
              <CustomAutocomplete
                options={refBank}
                label="Bank *"
                labelKey={"title"}
                value={
                  refBank.filter((b) => b.bank_id === dataDetail?.bank_id)[0]
                }
                onChange={(e, val) => handleChange("bank_id", val.bank_id)}
                disableClearable
                error={!!errors.bank_id}
                helperText={errors.bank_id}
              />
            </Grid>
          </Grid>

          <Stack direction={"column"}>
            <Typography
              variant="subtitle1"
              sx={{ mb: ".5rem", fontWeight: "bold" }}
            >
              {`Dokumen Lainnya (pdf) *`}
            </Typography>
            {dataDetail?.sign_contract_files?.map((sc) => (
              <Stack
                sx={{ mb: "1rem" }}
                direction="column"
                spacing={0.5}
                key={sc.job_posting_extension_form_id}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title={`Contoh berkas ${sc.name}`}>
                    <IconButton onClick={() => handleDownload(sc.file_path)}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Button
                    component="label"
                    variant="outlined"
                    sx={{ width: "fit-content", position: "relative" }}
                  >
                    {sc.name}
                    <VisuallyHiddenInput
                      type="file"
                      accept="application/pdf"
                      onChange={(e) =>
                        handleUploadBerkas(
                          e.target.files,
                          sc.job_posting_extension_form_id,
                          sc.uploaded_path,
                        )
                      }
                    />
                  </Button>
                  {sc.uploaded_path && <DoneOutline color="success" />}
                </Stack>
                {errors[
                  `sign_contract_${sc.job_posting_extension_form_id}`
                ] && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "12px" }}>
                    {
                      errors[
                        `sign_contract_${sc.job_posting_extension_form_id}`
                      ]
                    }
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loadingButton}
        >
          Simpan & Kirim
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default ModalSignContractRequired;
